import { twMerge } from "tailwind-merge";
import { Circle, CircleCheck } from "lucide-react";
import type { OnboardingStep } from "@core/features/onboarding";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";

const IncompleteIcon = ({ className }: { className?: string }) => (
  <Circle className={twMerge(className, "border-brand-600")} />
);

/** Styled Check icon for completed steps */
const CompletedIcon = ({ className }: { className?: string }) => (
  <CircleCheck
    className={twMerge(className, "text-brand-600")}
    strokeWidth={3}
  />
);

/** Styled Circle icon for current step indicator */
const CurrentIcon = ({ className }: { className?: string }) => (
  <Circle className={twMerge(className, "text-brand-600 fill-brand-600")} />
);

export type Props = {
  /** Step data */
  step: OnboardingStep;
  /** Whether this step is completed */
  isCompleted: boolean;
  /** Whether this is the current step */
  isCurrent: boolean;
  /** Whether this step was skipped */
  isSkipped: boolean;
  /** Whether this step is clickable */
  isClickable: boolean;
  /** Click handler */
  onClick?: () => void;
};

/**
 * Individual step item in the onboarding stepper.
 * Displays step number/check icon, label, and status indicators.
 *
 * @example
 * ```tsx
 * <OnboardingStepperItem
 *   index={0}
 *   step={{ slug: "profile", sequence: 1, required: true }}
 *   isCompleted={false}
 *   isCurrent={true}
 *   isSkipped={false}
 *   isClickable={true}
 *   onClick={() => console.log("Step clicked")}
 * />
 * ```
 */
export function OnboardingStepperItem({
  step,
  isCompleted,
  isCurrent,
  isSkipped,
  isClickable,
  onClick,
}: Props) {
  // Determine icon and position based on step state
  const iconComponent = isCurrent
    ? CurrentIcon
    : isCompleted
    ? CompletedIcon
    : IncompleteIcon;

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      disabled={!isClickable && !isCurrent}
      iconComponent={iconComponent}
      iconPosition={"left"}
      theme="dark"
      className={twMerge(
        "pr-4",
        isCurrent && "border-brand-100",
        !isClickable &&
          !isCurrent &&
          "bg-transparent border-none hover:bg-transparent"
      )}
    >
      {/* Step label */}
      <div className="flex flex-col">
        <Type
          variant="h6"
          as="span"
          className={twMerge("uppercase", isCompleted && "text-brand-50")}
        >
          {step.name ?? step.slug.replace(/_/g, " ")}
        </Type>
        {isSkipped && !isCompleted && !isCurrent && (
          <Type variant="caption">Skipped</Type>
        )}
      </div>
    </Button>
  );
}

export default OnboardingStepperItem;
